// Button Manager Utility for Fortisys Website
// Handles button interactions, states, and animations

export class ButtonManager {
  constructor(options = {}) {
    this.options = {
      rippleEffect: true,
      loadingDuration: 300,
      ...options
    };

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupButtonStates();
  }

  bindEvents() {
    // Handle all button clicks for ripple effect
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button, .btn');
      if (button && this.options.rippleEffect) {
        this.createRippleEffect(button, e);
      }
    });

    // Handle focus states for accessibility
    document.addEventListener('focusin', (e) => {
      const button = e.target.closest('button, .btn');
      if (button) {
        button.classList.add('btn--focused');
      }
    });

    document.addEventListener('focusout', (e) => {
      const button = e.target.closest('button, .btn');
      if (button) {
        button.classList.remove('btn--focused');
      }
    });

    // Handle hover states
    document.addEventListener('mouseover', (e) => {
      const button = e.target.closest('button, .btn');
      if (button && !button.disabled) {
        button.classList.add('btn--hover');
      }
    });

    document.addEventListener('mouseout', (e) => {
      const button = e.target.closest('button, .btn');
      if (button) {
        button.classList.remove('btn--hover');
      }
    });
  }

  setupButtonStates() {
    // Find all buttons and ensure proper attributes
    const buttons = document.querySelectorAll('button, .btn');

    buttons.forEach(button => {
      // Ensure proper ARIA attributes
      if (!button.hasAttribute('type') && button.tagName === 'BUTTON') {
        button.setAttribute('type', 'button');
      }

      // Add role for non-button elements with btn class
      if (button.tagName !== 'BUTTON' && button.tagName !== 'A') {
        button.setAttribute('role', 'button');
        button.setAttribute('tabindex', '0');
      }

      // Handle keyboard interactions for non-button elements
      if (button.tagName !== 'BUTTON') {
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            button.click();
          }
        });
      }
    });
  }

  createRippleEffect(button, event) {
    // Don't add ripple to disabled buttons
    if (button.disabled || button.classList.contains('btn--loading')) {
      return;
    }

    // Remove existing ripple
    const existingRipple = button.querySelector('.btn-ripple');
    if (existingRipple) {
      existingRipple.remove();
    }

    // Create ripple element
    const ripple = document.createElement('span');
    ripple.className = 'btn-ripple';

    // Calculate position and size
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      pointer-events: none;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      transform: scale(0);
      animation: ripple-animation 0.6s ease-out;
      z-index: 1;
    `;

    // Ensure button has relative positioning
    const buttonPosition = getComputedStyle(button).position;
    if (buttonPosition === 'static') {
      button.style.position = 'relative';
    }

    button.appendChild(ripple);

    // Remove ripple after animation
    setTimeout(() => {
      if (ripple.parentNode) {
        ripple.remove();
      }
    }, 600);
  }

  // Set loading state for a button
  setLoading(button, isLoading, loadingText = 'Loading...') {
    if (isLoading) {
      // Store original state
      button.dataset.originalText = button.textContent;
      button.dataset.originalDisabled = button.disabled;

      // Set loading state
      button.classList.add('btn--loading');
      button.disabled = true;
      button.textContent = loadingText;

      // Add loading spinner if needed
      this.addLoadingSpinner(button);
    } else {
      // Restore original state
      button.classList.remove('btn--loading');
      button.disabled = button.dataset.originalDisabled === 'true';
      button.textContent = button.dataset.originalText || button.textContent;

      // Remove loading spinner
      this.removeLoadingSpinner(button);
    }
  }

  addLoadingSpinner(button) {
    // Remove existing spinner
    const existingSpinner = button.querySelector('.btn-spinner');
    if (existingSpinner) {
      existingSpinner.remove();
    }

    // Create spinner element
    const spinner = document.createElement('span');
    spinner.className = 'btn-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    spinner.style.cssText = `
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-right: 8px;
    `;

    button.insertBefore(spinner, button.firstChild);
  }

  removeLoadingSpinner(button) {
    const spinner = button.querySelector('.btn-spinner');
    if (spinner) {
      spinner.remove();
    }
  }

  // Enhanced button click handler with debouncing
  handleClick(button, callback, debounceTime = 300) {
    if (button.dataset.clickTimeout) {
      return; // Prevent multiple rapid clicks
    }

    // Set debounce timeout
    button.dataset.clickTimeout = setTimeout(() => {
      delete button.dataset.clickTimeout;
    }, debounceTime);

    // Execute callback
    if (typeof callback === 'function') {
      callback(button);
    }
  }

  // Animate button on successful action
  animateSuccess(button, duration = 2000) {
    button.classList.add('btn--success');

    setTimeout(() => {
      button.classList.remove('btn--success');
    }, duration);
  }

  // Animate button on error
  animateError(button, duration = 2000) {
    button.classList.add('btn--error');

    setTimeout(() => {
      button.classList.remove('btn--error');
    }, duration);
  }

  // Add CSS animations if not already present
  injectStyles() {
    if (document.querySelector('#button-manager-styles')) {
      return; // Styles already injected
    }

    const style = document.createElement('style');
    style.id = 'button-manager-styles';
    style.textContent = `
      @keyframes ripple-animation {
        to {
          transform: scale(2);
          opacity: 0;
        }
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .btn {
        position: relative;
        overflow: hidden;
        transition: all 0.3s ease;
      }

      .btn--loading {
        cursor: wait;
        opacity: 0.7;
      }

      .btn--focused {
        outline: 2px solid var(--color-primary, #1a365d);
        outline-offset: 2px;
      }

      .btn--success {
        background-color: var(--color-success, #28a745) !important;
        transform: scale(1.05);
      }

      .btn--error {
        background-color: var(--color-error, #dc3545) !important;
        animation: shake 0.5s ease-in-out;
      }

      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
      }

      .btn-ripple {
        position: absolute;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.6);
        pointer-events: none;
        transform: scale(0);
        animation: ripple-animation 0.6s ease-out;
      }

      /* Form message styles */
      .form-message {
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 16px;
        opacity: 0;
        transform: translateY(-10px);
        transition: all 0.3s ease;
      }

      .form-message--visible {
        opacity: 1;
        transform: translateY(0);
      }

      .form-message--success {
        background-color: #d4edda;
        border: 1px solid #c3e6cb;
        color: #155724;
      }

      .form-message--error {
        background-color: #f8d7da;
        border: 1px solid #f5c6cb;
        color: #721c24;
      }

      .form-message--info {
        background-color: #d1ecf1;
        border: 1px solid #bee5eb;
        color: #0c5460;
      }
    `;

    document.head.appendChild(style);
  }

  destroy() {
    // Remove injected styles
    const styles = document.querySelector('#button-manager-styles');
    if (styles) {
      styles.remove();
    }

    // Clear any pending timeouts
    const buttonsWithTimeouts = document.querySelectorAll('[data-click-timeout]');
    buttonsWithTimeouts.forEach(button => {
      if (button.dataset.clickTimeout) {
        clearTimeout(button.dataset.clickTimeout);
        delete button.dataset.clickTimeout;
      }
    });
  }
}
